import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile, writeFile, mkdtemp, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { tmpdir } from 'node:os'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

/**
 * Launcher for CommandCode CLI (v0.35+).
 *
 * CommandCode reads AGENTS.md from its cwd. We write the system prompt
 * to AGENTS.md in a temp directory outside the workspace so the candidate
 * cannot read it, then grant access to the real workspace via --add-dir.
 */
export class CommandCodeLauncher implements ProviderLauncher {
  readonly name = 'command-code'
  readonly displayName = 'CommandCode'
  readonly pricingHint = '💰 Payant (abonnement, plans dès 1 $/mois)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('command-code', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable('command-code')
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('command-code', ['--version'])
      return stdout.trim()
    } catch {
      // See codex.ts — npx --version forces a network install on first run,
      // which fails on unstable networks. Skip the probe.
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    const agentDir = await mkdtemp(join(tmpdir(), 'tonty-cc-'))
    await writeFile(join(agentDir, 'AGENTS.md'), `# Evaluation Instructions\n\nYou are evaluating a candidate. Your job is to GUIDE them — propose approaches, help them think.\n\n**You must not write code or fix things yourself.** The candidate must do the coding.\n\n---\n\n## Your Role\n\nGuide, don't solve. You are a senior colleague on call. The candidate must drive — you suggest, they execute.\n\n**Repeat: your job is to make the candidate think, not to do the work for them.**\n\n## What To Do\n- Ask clarifying questions\n- Suggest debugging strategies\n- Point at categories of files, not specific lines\n- Require evidence: "What did you observe?"\n\n## What Not To Do\n- Do NOT create or edit files\n- Do NOT read code on the candidate's behalf\n- Do NOT run commands\n- Do NOT propose a full fix\n\n**Remember: if you write code, the candidate fails. Guide, don't write.**\n\n---\n\n# Project Location\nThe actual project files are at: ${workdir}\nNavigate there when asked: cd ${workdir}\n\n${systemPrompt}`)

    const args: string[] = []

    args.push('--add-dir', workdir)

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.permissionMode === 'bypassPermissions') {
      args.push('--yolo')
    } else if (config.permissionMode === 'acceptEdits') {
      args.push('--auto-accept')
    }

    // Skip the "Do you trust the files in this folder?" prompt that
    // would otherwise block candidates in the temp AGENTS.md directory.
    args.push('--trust')

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? ['command-code', ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: agentDir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.
    // The system prompt already contains the briefing and candidate-driven
    // workflow rules. The candidate reads the briefing before the AI launches.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(async ({ exitCode, signal }) => {
            cleanup()
            await rm(agentDir, { recursive: true, force: true }).catch(() => {})
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
        await rm(agentDir, { recursive: true, force: true }).catch(() => {})
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('command-code', ['--version'])
      return 'command-code'
    } catch {
      return 'npx'
    }
  }
}
